import { create } from 'zustand'

import type { PublicBranding } from '@/api/types'
import { darken, isHexColor, readableForeground } from './color'

/** The product name, as the client wrote it (launch spec §1). */
export const PLATFORM_PRODUCT_NAME = 'RecoupSlots'

/**
 * The client's official logo (launch spec §2), shipped as a static frontend asset:
 *   public/brand/recoupslots-logo-original.jpeg  the file exactly as supplied (flattened on black)
 *   public/brand/recoupslots-logo.png            the same mark with the black background keyed to
 *                                                transparent and trimmed — nothing redrawn
 *   public/brand/recoupslots-mark.png            the calendar glyph alone (compact rail, favicon)
 * The resolved platform brand keeps `logo_asset_url` null; `platformWordmarkUrl` substitutes this
 * asset for the platform brand only, so a tenant's own logo is never replaced.
 */
export const PLATFORM_LOGO_URL = '/brand/recoupslots-logo.png'
export const PLATFORM_MARK_URL = '/brand/recoupslots-mark.png'

/**
 * The backend's platform defaults (`DEFAULT_PLATFORM_BRANDING`, migration
 * 20261009090000_recoupslots_brand) — what an unknown host resolves to, and what paints before
 * `/branding/resolve` answers.
 */
export const PLATFORM_BRAND: PublicBranding = {
  owner_type: 'platform',
  product_name: PLATFORM_PRODUCT_NAME,
  tagline: 'Turn open time into booked time.',
  logo_asset_url: null,
  logo_mark_letter: 'R',
  primary_color: '#11243A',
  accent_color: '#16B8A6',
  show_powered_by: true,
}

/**
 * True when the resolved brand IS the platform (the RecoupSlots host, or a cached brand from
 * before `owner_type` existed). A tenant's custom domain resolves to its own branding.
 */
export function isPlatformBrand(brand: PublicBranding): boolean {
  // Older cached brands have no owner_type; they can only have come from a host that resolved to
  // the platform defaults, so treat "unknown" as platform.
  return (brand.owner_type as string | undefined) === undefined || brand.owner_type === 'platform'
}

/**
 * The wide RecoupSlots wordmark, for the platform brand that has no logo of its own. A tenant brand
 * (its own logo or the letter mark) returns null and keeps the mark + name rendering.
 */
export function platformWordmarkUrl(brand: PublicBranding): string | null {
  if (brand.logo_asset_url !== null && brand.logo_asset_url !== '') return null
  return isPlatformBrand(brand) ? PLATFORM_LOGO_URL : null
}

/** "Powered by RecoupSlots" appears on a tenant's pages only — the platform never powers itself. */
export function showPoweredBy(brand: PublicBranding): boolean {
  return (
    brand.show_powered_by &&
    brand.product_name.trim().toLowerCase() !== PLATFORM_PRODUCT_NAME.toLowerCase()
  )
}

export const POWERED_BY_LABEL = `Powered by ${PLATFORM_PRODUCT_NAME}`

export type BrandSource = 'default' | 'cache' | 'resolved'

interface BrandState {
  readonly brand: PublicBranding
  readonly source: BrandSource
  setBrand: (brand: PublicBranding, source: BrandSource) => void
}

export const useBrandStore = create<BrandState>()((set) => ({
  brand: PLATFORM_BRAND,
  source: 'default',
  setBrand: (brand, source) => {
    set({ brand, source })
  },
}))

export interface BrandVariables {
  readonly '--brand-primary': string
  readonly '--brand-primary-deep': string
  readonly '--brand-primary-fg': string
  readonly '--brand-accent': string
  readonly '--brand-accent-fg': string
}

/** CSS variables for a tenant palette, with foregrounds chosen for contrast (FRONTEND_PLAN §6.1). */
export function brandVariables(brand: PublicBranding): BrandVariables {
  const primary = isHexColor(brand.primary_color)
    ? brand.primary_color
    : PLATFORM_BRAND.primary_color
  const accent = isHexColor(brand.accent_color) ? brand.accent_color : PLATFORM_BRAND.accent_color
  return {
    '--brand-primary': primary,
    '--brand-primary-deep': darken(primary, 0.28),
    '--brand-primary-fg': readableForeground(primary),
    '--brand-accent': accent,
    '--brand-accent-fg': readableForeground(accent),
  }
}

/** Writes the variables on <html> and updates the document title. */
export function applyBrand(
  brand: PublicBranding,
  root: HTMLElement = document.documentElement,
): void {
  const variables: Record<string, string> = { ...brandVariables(brand) }
  for (const [name, value] of Object.entries(variables)) root.style.setProperty(name, value)
  document.title = brand.product_name
}
